/**
 * Señales semánticas derivadas solo desde datos del menú (nombre, descripción,
 * categoría, agregados, promos ligadas). Sin IA externa.
 */

function stripAccents(s) {
  return String(s || '')
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

export function normalizeMenuBlob(s) {
  return stripAccents(String(s || '').toLowerCase());
}

function addonNamesBlob(product) {
  const a = product?.add_ons;
  if (!Array.isArray(a) || !a.length) return '';
  return a
    .map((x) => (typeof x === 'string' ? x : x?.name || ''))
    .filter(Boolean)
    .join(' ');
}

function removableBlob(product) {
  const r = product?.removable_ingredients;
  if (!Array.isArray(r) || !r.length) return '';
  return r.map((x) => (typeof x === 'string' ? x : x?.name || '')).filter(Boolean).join(' ');
}

function linkedPromoBlob(product, promos) {
  const pid = String(product?.id ?? '');
  if (!pid) return '';
  const parts = [];
  for (const pr of promos || []) {
    if (pr?.is_active === false) continue;
    const ids = pr?.product_ids;
    if (!Array.isArray(ids) || !ids.some((id) => String(id) === pid)) continue;
    parts.push(pr.name, pr.description);
  }
  return parts.filter(Boolean).join(' ');
}

function fullBlobForSemantics(product, business, promos) {
  const incRaw = product?.included_items ?? product?.includedItems;
  const incSummary = product?.included_summary ?? product?.includedSummary;
  const chunks = [
    product?.name,
    product?.description,
    product?.category,
    incSummary != null && String(incSummary).trim() ? String(incSummary).trim() : null,
    Array.isArray(incRaw) && incRaw.length ? incRaw.map((x) => String(x ?? '').trim()).filter(Boolean).join(' ') : null,
    removableBlob(product),
    addonNamesBlob(product),
    linkedPromoBlob(product, promos),
    business?.name,
    business?.description,
  ];
  return normalizeMenuBlob(chunks.filter(Boolean).join(' '));
}

/** Orden: match más largo gana (misma idea que PRODUCT_FAMILY_ALIASES). */
const FAMILY_RULES = [
  { id: 'hamburguesa', match: ['smash burger', 'hamburguesas', 'hamburguesa', 'hamburg', 'burger', 'burgers', 'smash'] },
  { id: 'sushi', match: ['omakase', 'uramaki', 'hosomaki', 'futomaki', 'gunkan', 'sashimi', 'nigiri', 'makis', 'maki', 'sushi'] },
  { id: 'hand_roll', match: ['hand rolls', 'handroll', 'handrolls', 'temaki'] },
  { id: 'pizza', match: ['pizza', 'piza'] },
  { id: 'parrilla', match: ['picanha', 'entraña', 'entrania', 'parrillada', 'parrillero', 'parrilla', 'churrasco', 'asado de tira'] },
  { id: 'completo', match: ['hot dog', 'hotdog', 'vienesa', 'vienesas', 'completos', 'completo'] },
  { id: 'sandwich', match: ['sándwiches', 'sandwiches', 'sándwich', 'sandwich', 'panini', 'club sandwich'] },
  { id: 'mex', match: ['quesadillas', 'quesadilla', 'burritos', 'burrito', 'nachos', 'nacho', 'tacos', 'taco', 'taquitos', 'taquito'] },
  { id: 'poke', match: ['pokes', 'poke'] },
  { id: 'pollo_frito', match: ['crispy chicken', 'broasted', 'broaster', 'alitas', 'wings', 'wing', 'pollo frito', 'pollo crispy'] },
  { id: 'pollo_asado', match: ['pollo al spiedo', 'pollo spiedo', 'pollo a la brasa', 'pollo brasa', 'pollo asado', 'pollo al horno'] },
  { id: 'papas_topping', match: ['loaded fries', 'papas cargadas', 'papas locas', 'papas topping', 'papas con topping'] },
  { id: 'cerveza', match: ['chela', 'cervezas', 'cerveza', 'schop', 'ipa', 'lager', 'stout', 'pilsen'] },
  { id: 'licor', match: ['mojito', 'tequila', 'tragos', 'trago', 'coctel', 'cóctel', 'cocktail', 'whisky', 'whiskey', 'pisco', 'bar'] },
  { id: 'colaciones', match: ['pack escolar', 'lonchera', 'colaciones', 'colacion', 'despacho escolar'] },
  { id: 'empanada', match: ['empanadas', 'empanada'] },
  { id: 'pasta', match: ['gnocchi', 'ñoquis', 'ravioles', 'ravioli', 'lasagna', 'fettuccine', 'fetuccini', 'spaghetti', 'pasta'] },
  { id: 'ramen', match: ['ramens', 'ramen'] },
  { id: 'wok', match: ['chop suey', 'fideos salteados', 'wok'] },
  { id: 'mechada', match: ['chacarero', 'italiano chileno', 'barros luco', 'lomito mechado', 'carne mechada', 'mechada'] },
  { id: 'platos_preparados', match: ['menu ejecutivo', 'menú ejecutivo', 'platos preparados', 'plato preparado'] },
].map((f) => ({
  id: f.id,
  match: [...new Set(f.match.map((k) => normalizeMenuBlob(k)).filter((k) => k.length >= 2))].sort((a, b) => b.length - a.length),
}));

function wordMatch(blob, phrase) {
  const p = String(phrase || '').trim();
  if (!blob || !p || p.length < 2) return false;
  if (p.includes(' ')) {
    const esc = p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const rx = new RegExp(
      '(?:^|[^a-z0-9])' + esc.split(/\s+/).join('\\s+') + '(?:$|[^a-z0-9])',
      'i'
    );
    return rx.test(blob);
  }
  return new RegExp('\\b' + p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b', 'i').test(blob);
}

function resolveFamily(blob) {
  let best = null;
  let bestLen = -1;
  for (const fam of FAMILY_RULES) {
    for (const m of fam.match) {
      if (m.length < 2) continue;
      if (wordMatch(blob, m) && m.length > bestLen) {
        best = fam.id;
        bestLen = m.length;
      }
    }
  }
  return best;
}

/** Familia de producto inferida desde un texto (nombre de categoría, local, etc.). */
export function resolveMenuFamilyFromText(text) {
  return resolveFamily(normalizeMenuBlob(text));
}

/** Igual que resolveMenuFamilyFromText, pero tolera plurales y nombres compuestos de categoría. */
export function resolveMenuFamilyFromTextLoose(text) {
  const strict = resolveMenuFamilyFromText(text);
  if (strict) return strict;
  const n = normalizeMenuBlob(text);
  let best = null;
  let bestLen = -1;
  for (const fam of FAMILY_RULES) {
    for (const m of fam.match) {
      if (m.length < 2) continue;
      if (n.includes(m) && m.length > bestLen) {
        best = fam.id;
        bestLen = m.length;
      }
    }
  }
  return best;
}

const SPICY_PATTERNS = [
  /\bpicante\b/i,
  /\bspicy\b/i,
  /\baj[ií]\b/i,
  /\baji\b/i,
  /\bbuffalo\b/i,
  /\bchipotle\b/i,
  /\bjalape[nñ]o\b/i,
  /\bmerk[eé]n\b/i,
  /\bsriracha\b/i,
  /\benchilad\w*\b/i,
  /\bchile\s+(?:morita|de\s+árbol|habanero)\b/i,
  /\bhabanero\b/i,
  /\bghost\s*pepper\b/i,
  /\bwasabi\b/i,
  /\bgochujang\b/i,
  /\bhot\s+sauce\b/i,
];

/** “hot” solo en contexto picante, no hot dog */
function spicyFromBlob(blob) {
  const b = String(blob || '');
  if (SPICY_PATTERNS.some((rx) => rx.test(b))) return true;
  if (/\bextra\s+hot\b/i.test(b) || /\bsuper\s+hot\b/i.test(b)) return true;
  if (/\bhot\b/i.test(b) && /\b(chicken|wings|salsa|sauce|buffalo)\b/i.test(b)) return true;
  return false;
}

const DRINK_RE =
  /\b(bebida|gaseosa|jugo|coca|sprite|pepsi|fanta|red\s*bull|energizante|agua\s+mineral|schop|cerveza|refresco|lata|500\s*cc|litro|lt\b|bebidas)\b/i;
const FRIES_RE =
  /\b(papa|papas|frita|fritas|papas\s+fritas|porci[oó]n\s+de\s+papas|french\s*fries)\b/i;

const COMBO_HINT =
  /\b(combo|pack|men[uú]\s+completo|menu\s+completo|promo\s+armad|arma\s+tu\s+men|men[uú]\s+con\s+papas|incluye\s+papas|incluye\s+bebida|con\s+bebida|con\s+papas|viene\s+con\s+papas|\+bebida|\+papas|2x1|3x2)\b/i;

const SHARED_HINT =
  /\b(para\s*2|para\s+dos|para\s*tres|para\s*3|duo|d[uú]o|familiar|compartir|pack\s+familiar|grupo|2\s*pers|3\s*pers|tres\s+personas|bandeja|tabla|plato\s+familiar|porci[oó]n\s+familiar)\b/i;

const SLOW_PREP_HINT =
  /\b(parrill|asado\b|hornead|pizza\b|sushi|maki\b|omakase|ramen|paella|lasa[nñ]a|horno\s+lento|a\s+la\s+brasa|al\s+horno\b|cocinad\w*\s+lento|wok\b|pasta\b|fetuccini|ravioles)\b/i;

const FAST_HINT =
  /\b(gaseosa|jugo\b|bebida\b|agua\s+mineral|empanada|completo|hotdog|hot\s+dog|helado|caf[eé]|donut|muffin|jugo\s+natural|bebidas?\s+en\s+lata)\b/i;

function detectDrinkFriesFromAddons(product, blob) {
  const addBlob = normalizeMenuBlob(addonNamesBlob(product));
  const fromText = DRINK_RE.test(blob) || DRINK_RE.test(addBlob);
  const fromFries = FRIES_RE.test(blob) || FRIES_RE.test(addBlob);
  return { drink: fromText, fries: fromFries };
}

function collectProteinTags(blob) {
  const tags = [];
  const push = (t) => {
    if (t && !tags.includes(t)) tags.push(t);
  };
  if (/\b(pollo|chicken|broaster|alita|wing|nugget|karaage|katsu)\b/i.test(blob)) push('pollo');
  if (/\b(carne|vacuno|res|beef|lomito|wagyu|churrasco)\b/i.test(blob)) push('carne');
  if (/\b(pescado|fish|salm[oó]n|sake\b|at[uú]n|maguro|corvina|reineta)\b/i.test(blob)) push('pescado');
  if (/\b(cerdo|pork|chancho|costillar|ribs|panceta|bacon|tocino)\b/i.test(blob)) push('cerdo');
  if (/\b(camaron|camar[oó]n|shrimp|ebi|langostino|pulpo|tako\b|cangrejo|crab|kani)\b/i.test(blob)) push('marisco');
  return tags;
}

function collectStyleTags(blob) {
  const tags = [];
  const push = (t) => {
    if (t && !tags.includes(t)) tags.push(t);
  };
  if (/\bcrispy\b/i.test(blob) || /\bcrujiente\b/i.test(blob)) push('crispy');
  if (/\btempura\b/i.test(blob)) push('tempura');
  if (/\bfurai\b/i.test(blob) || /\bpanko\b/i.test(blob)) push('furai');
  if (/\bgrill(ed)?\b/i.test(blob) || /\bparrill\w*\b/i.test(blob) || /\bbrasa\b/i.test(blob)) push('grilled');
  if (/\bfrito\b/i.test(blob) || /\bfritos\b/i.test(blob) || /\bempanizad\w*\b/i.test(blob)) push('fried');
  if (/\bahumad\w*\b/i.test(blob) || /\bsmoked\b/i.test(blob)) push('smoked');
  return tags;
}

function collectAddonTags(product) {
  const a = product?.add_ons;
  if (!Array.isArray(a)) return [];
  const out = [];
  for (const x of a) {
    const name = typeof x === 'string' ? x : x?.name;
    const n = normalizeMenuBlob(name || '').trim();
    if (n.length >= 2 && !out.includes(n)) out.push(n);
  }
  return out.slice(0, 12);
}

function dietaryFromBlob(blob) {
  const b = String(blob || '');
  const is_vegan = /\bvegan\w*\b/i.test(b);
  const is_vegetarian =
    /\bvegetarian\w*\b/i.test(b) || /\bveggie\b/i.test(b) || (/\bvegetal(es)?\b/i.test(b) && !/\bpollo\b/i.test(b));
  const is_gluten_free =
    /\bsin\s+gluten\b/i.test(b) || /\bgluten\s*free\b/i.test(b) || /\bsin\s+tacc\b/i.test(b) || /\bceliac/i.test(b);
  return { is_vegan, is_vegetarian, is_gluten_free };
}

/**
 * @param {object} product
 * @param {object} [business]
 * @param {object[]} [promos]
 * @returns {{
 *   is_combo: boolean,
 *   is_shared: boolean,
 *   is_fast: boolean,
 *   is_spicy: boolean,
 *   has_drink: boolean,
 *   has_fries: boolean,
 *   is_vegetarian: boolean,
 *   is_vegan: boolean,
 *   is_gluten_free: boolean,
 *   family: string | null,
 *   protein_tags: string[],
 *   style_tags: string[],
 *   addon_tags: string[]
 * }}
 */
export function deriveProductSemanticTags(product, business = null, promos = null) {
  const safePromos = (promos || []).filter((p) => p?.is_active !== false);
  const blob = fullBlobForSemantics(product, business, safePromos);
  const { drink: drinkAddon, fries: friesAddon } = detectDrinkFriesFromAddons(product, blob);

  const has_drink = drinkAddon || /\bincluye\s+bebida\b/i.test(blob) || /\bcon\s+gaseosa\b/i.test(blob);
  const has_fries = friesAddon || /\bincluye\s+papas\b/i.test(blob) || /\bcon\s+papas\b/i.test(blob);

  let is_combo =
    COMBO_HINT.test(blob) ||
    (has_drink && has_fries) ||
    (/\b(incluye|viene\s+con)\b/i.test(blob) &&
      /\b(bebida|gaseosa|jugo)\b/i.test(blob) &&
      /\b(papa|papas|frita)\b/i.test(blob));

  const is_shared = SHARED_HINT.test(blob);

  let is_fast = FAST_HINT.test(blob) && !SLOW_PREP_HINT.test(blob);
  if (is_combo || is_shared) is_fast = false;

  const attrs = new Set(
    (Array.isArray(product?.attributes) ? product.attributes : []).map((x) => String(x || '').trim()),
  );
  const is_spicy = attrs.has('spicy') || spicyFromBlob(blob);

  const diet = dietaryFromBlob(blob);
  const family = resolveFamily(blob);

  return {
    is_combo: Boolean(is_combo),
    is_shared: Boolean(is_shared),
    is_fast: Boolean(is_fast),
    is_spicy: Boolean(is_spicy),
    has_drink: Boolean(has_drink),
    has_fries: Boolean(has_fries),
    is_vegetarian: attrs.has('vegetarian') || attrs.has('vegan') || diet.is_vegetarian,
    is_vegan: attrs.has('vegan') || diet.is_vegan,
    is_gluten_free: attrs.has('gluten_free') || diet.is_gluten_free,
    family,
    protein_tags: collectProteinTags(blob),
    style_tags: collectStyleTags(blob),
    addon_tags: collectAddonTags(product),
  };
}

/** Para capa contextual: mapear family id → claves usadas en complementKeywordPatternForFamilies */
export function complementFamiliesFromSemanticFamily(familyId) {
  if (!familyId) return [];
  const map = {
    hamburguesa: ['burger'],
    arma_burger: ['burger'],
    sushi: ['sushi'],
    hand_roll: ['sushi'],
    poke: ['sushi'],
    pizza: ['pizza'],
    pollo_frito: ['chicken'],
    pollo_asado: ['chicken'],
    mex: ['mex'],
    completo: ['completo'],
    parrilla: ['grill'],
    mechada: ['grill'],
    sandwich: ['burger'],
    pasta: ['pizza'],
    wok: ['sushi'],
    ramen: ['sushi'],
    empanada: [],
    papas_topping: [],
    cerveza: [],
    licor: [],
    colaciones: [],
    platos_preparados: [],
  };
  return map[familyId] || [];
}

/**
 * Evita sugerir bebida/papas/combo duplicado respecto al producto principal.
 */
export function suggestionConflictsWithMainSemanticTags(candidateBlob, mainTags) {
  if (!mainTags) return false;
  const c = normalizeMenuBlob(candidateBlob || '');
  const isDrink = DRINK_RE.test(c);
  const isFries = FRIES_RE.test(c);
  const isComboLike = /\b(combo|men[uú]\s+combo|pack\s+familiar|pack\s+duo)\b/i.test(c);
  if (mainTags.has_drink && isDrink) return true;
  if (mainTags.has_fries && isFries) return true;
  if (mainTags.is_combo && isComboLike) return true;
  return false;
}
